'use client'

import { RESTRO_REGISTER_URL } from '@/config/portal'
import AnimatedChatDemo, { type ChatLine } from './AnimatedChatDemo'
import '@/styles/ai-showcase.css'

/** Customer WhatsApp flow: budget → picks → order → track (chat / voice). */
const RECEPTIONIST_LINES: ChatLine[] = [
  {
    from: 'user',
    kind: 'voice',
    text: 'Voice · “Something nice for around $12”',
    time: '7:42 PM',
  },
  {
    from: 'ai',
    kind: 'items',
    text: 'Here are two options near $12:',
    time: '7:42 PM',
    items: [
      { name: '1. Zinger box + drink', price: '$11.50', note: 'Ready in ~20 min' },
      { name: '2. Chicken wrap meal', price: '$10.90', note: 'Most ordered today' },
    ],
  },
  { from: 'user', text: 'I’ll take option 1 — deliver to my saved address.', time: '7:43 PM' },
  {
    from: 'ai',
    text: 'Order #4821 placed. Zinger box + drink · $11.50. Pay on delivery.',
    time: '7:43 PM',
  },
  { from: 'user', text: 'Where is my food?', time: '8:01 PM' },
  {
    from: 'ai',
    kind: 'track',
    time: '8:01 PM',
    track: {
      title: 'On the way · Order #4821',
      detail: 'Rider Bilal is about 7 minutes away. You can ask by chat or send another voice note anytime.',
    },
  },
]

/** Owner Farori — talk about the whole restaurant. */
const ASSISTANT_LINES: ChatLine[] = [
  { from: 'user', text: 'How much revenue did we make this week?', time: '3:10 PM' },
  {
    from: 'ai',
    text: 'This week: $18,420 across all branches. Up 9% from last week. F-10 led with $6,100.',
    time: '3:10 PM',
  },
  { from: 'user', text: 'Any unpaid supplier bills?', time: '3:11 PM' },
  {
    from: 'ai',
    text: 'Yes — two open bills: Metro Foods $840 (due Friday) and Fresh Farm $320 (due Monday).',
    time: '3:11 PM',
  },
  { from: 'user', text: 'What’s booked for the weekend event?', time: '3:12 PM' },
  {
    from: 'ai',
    text: 'Saturday catering for 80 guests is confirmed. Deposit received. Kitchen prep list is ready in orders.',
    time: '3:12 PM',
  },
]

/** AI Receptionist (customers) + Farori (owners). */
export default function AiShowcaseSections() {
  return (
    <div className="ai-showcase-wrap">
      {/* Farori first — owners, keep liked dark design */}
      <section className="ai-farori" aria-labelledby="ai-assistant-title">
        <div className="ar-container ai-farori__grid">
          <div className="ai-farori__chat-wrap">
            <p className="ai-farori__chat-label">Inside your dashboard</p>
            <AnimatedChatDemo
              name="Farori"
              initials="F"
              status="ready"
              lines={ASSISTANT_LINES}
              variant="farori"
            />
          </div>

          <div className="ai-farori__copy">
            <p className="ai-farori__eyebrow">AI Assistant · Farori · $19 / mo · all branches</p>
            <h2 id="ai-assistant-title" className="ai-farori__title">
              Ask about your restaurant like you would ask a manager.
            </h2>
            <p className="ai-farori__lead">
              Farori is for restaurant owners and managers. Talk about sales, orders, accounts,
              revenue, stock, staff, or events — in plain words. Type or use voice. You get short
              answers from your live data inside AI Restro 360.
            </p>
            <div className="ai-farori__tags" role="list">
              <span role="listitem">Sales & revenue</span>
              <span role="listitem">Orders</span>
              <span role="listitem">Accounts & bills</span>
              <span role="listitem">Events</span>
              <span role="listitem">Stock & staff</span>
            </div>
            <a
              className="ai-farori__cta"
              href={RESTRO_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Farori
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp ordering for customers — phone frame */}
      <section className="ai-wa" aria-labelledby="ai-receptionist-title">
        <div className="ar-container">
          <div className="ai-wa__grid">
            <div className="ai-wa__copy">
              <p className="ai-wa__eyebrow">AI Receptionist · WhatsApp · $19 / mo · all branches</p>
              <h2 id="ai-receptionist-title" className="ai-wa__title">
                Customers order by talking on WhatsApp. No forms. No menus to tap through.
              </h2>
              <p className="ai-wa__lead">
                Guests send a chat, a voice note, or a photo. The AI suggests items for their budget,
                places the order, and answers “where is my food?” — by chat or voice. Your team only
                steps in when needed. No free trial on this part.
              </p>
              <a
                className="ai-wa__cta"
                href={RESTRO_REGISTER_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Add WhatsApp ordering
              </a>
            </div>

            <div className="ai-wa__phone-wrap">
              <div className="ai-wa__phone">
                <div className="ai-wa__phone-notch" aria-hidden />
                <AnimatedChatDemo
                  name="Your restaurant"
                  initials="WA"
                  status="online"
                  lines={RECEPTIONIST_LINES}
                  variant="wa"
                />
              </div>
              <p className="ai-wa__caption">Chat · voice · track — sample guest flow</p>
            </div>
          </div>

          <ol className="ai-wa__steps">
            <li>
              <div className="ai-wa__step-body">
                <strong>Say what they want</strong>
                <span>“Something for around $12” — typed or spoken.</span>
              </div>
            </li>
            <li>
              <div className="ai-wa__step-body">
                <strong>Pick from a short list</strong>
                <span>AI shows a few matching items with prices.</span>
              </div>
            </li>
            <li>
              <div className="ai-wa__step-body">
                <strong>Order & track</strong>
                <span>One message confirms. Later they ask for delivery status anytime.</span>
              </div>
            </li>
          </ol>
        </div>
      </section>
    </div>
  )
}
